import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, TrendingUp } from 'lucide-react';
import {
  useImpactRecords,
  ImpactCategory,
  ImpactRecord,
  IMPACT_CATEGORY_LABELS,
} from '@/hooks/useImpactRecords';
import { ImpactStats } from '@/components/public/ImpactStats';
import { ImpactCard } from '@/components/public/ImpactCard';
import { ImpactDetailModal } from '@/components/public/ImpactDetailModal';
import TestimonialsCarousel from '@/components/public/TestimonialsCarousel';
import { SEO } from '@/components/SEO';

type Filter = 'all' | ImpactCategory;

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'conferencia_taller', label: IMPACT_CATEGORY_LABELS.conferencia_taller },
  { value: 'proyecto', label: IMPACT_CATEGORY_LABELS.proyecto },
  { value: 'caso_exito', label: IMPACT_CATEGORY_LABELS.caso_exito },
];

export default function ImpactPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ImpactRecord | null>(null);

  const { data: records = [], isLoading } = useImpactRecords(
    filter === 'all' ? {} : { category: filter },
  );

  const filtered = useMemo(() => {
    const base = records.filter((r) => r.category !== 'testimonio');
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) =>
      [r.title, r.subtitle, r.description, r.company, r.location]
        .filter(Boolean)
        .some((s) => (s as string).toLowerCase().includes(q)),
    );
  }, [records, search]);

  return (
    <div className="container mx-auto px-4 py-12 max-w-7xl space-y-12">
      <SEO
        title="Casos de impacto con IA — LÍDER IA"
        description="Conoce proyectos, casos de éxito y testimonios de profesionales y empresas que han transformado su trabajo con IA."
        path="/impacto"
      />
      <header className="text-center space-y-4">
        <Badge variant="outline" className="mb-2">
          <TrendingUp className="h-3 w-3 mr-1" />
          Nuestro Impacto
        </Badge>
        <h1 className="text-3xl md:text-5xl font-bold">
          Transformando Profesionales y Empresas
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Conoce las conferencias, proyectos y casos de éxito que han marcado la transformación con IA.
        </p>
      </header>

      <ImpactStats />

      <div className="space-y-6">
        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <Button
                key={f.value}
                variant={filter === f.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </Button>
            ))}
          </div>
          <div className="relative md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            No hay registros de impacto en esta categoría.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((r) => (
              <ImpactCard key={r.id} record={r} onClick={setSelected} />
            ))}
          </div>
        )}
      </div>

      <TestimonialsCarousel />

      <ImpactDetailModal record={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
